import React, { useState, useEffect, useRef } from 'react';
import { Modal, Card, Button, Typography, Space, Input, InputNumber, message, Tooltip } from 'antd';
import { BarcodeOutlined, CopyOutlined, DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import JsBarcode from 'jsbarcode';

const { Title, Text, Paragraph } = Typography;

const BarcodeModal = ({ visible, onClose, item, facilityId }) => {
  const [barcodeSize, setBarcodeSize] = useState(2);
  const [quantity, setQuantity] = useState(1);
  const canvasRef = useRef(null);
  
  // Get barcode value safely (hooks must be called before any returns)
  const barcodeValue = item?.barcode || item?.baseId || item?.name || '';
  const maxQuantity = Math.min(16, item?.quantity || 1);
  
  // Reset quantity when modal opens or item changes
  useEffect(() => {
    if (visible && item) {
      const maxQty = Math.min(16, item.quantity || 1);
      setQuantity((prevQty) => Math.min(prevQty || 1, maxQty));
    }
  }, [visible, item]);
  
  useEffect(() => {
    if (visible && canvasRef.current && barcodeValue && item && facilityId) {
      try {
        // Clear canvas
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Generate barcode
        JsBarcode(canvas, barcodeValue, {
          format: "CODE128", // Common barcode format
          width: barcodeSize,
          height: 80,
          displayValue: true, // Show numbers below barcode
          fontSize: 16,
          textMargin: 4,
          margin: 10,
          background: "#ffffff",
          lineColor: "#000000"
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
        message.error('Failed to generate barcode');
      }
    }
  }, [visible, barcodeValue, barcodeSize, item, facilityId]);

  // Early return after all hooks
  if (!item || !facilityId) return null;

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${type} copied to clipboard!`);
    }).catch(() => {
      message.error('Failed to copy to clipboard');
    });
  };

  const downloadBarcode = () => {
    if (canvasRef.current && item) {
      const link = document.createElement('a');
      link.download = `${item.name || 'item'}-barcode.png`;
      link.href = canvasRef.current.toDataURL();
      link.click();
    }
  };

  const generateA4Page = () => {
    if (!barcodeValue || !item) return;

    // A4 dimensions at 96 DPI (web standard)
    const A4_WIDTH = 794;  // 210mm at 96 DPI
    const A4_HEIGHT = 1123; // 297mm at 96 DPI
    const GAP = 10; // Gap between barcodes
    
    // Barcode dimensions (adjusted to fit 2 columns)
    const BARCODE_WIDTH = (A4_WIDTH - (GAP * 3)) / 2; // 2 columns with gaps
    const BARCODE_HEIGHT = 120; // Height per barcode
    
    // Calculate grid layout
    const cols = 2;
    const rows = Math.ceil(quantity / cols);
    
    // Create A4 canvas
    const canvas = document.createElement('canvas');
    canvas.width = A4_WIDTH;
    canvas.height = A4_HEIGHT;
    const ctx = canvas.getContext('2d');
    
    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, A4_WIDTH, A4_HEIGHT);
    
    // Create temporary canvas for single barcode
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = BARCODE_WIDTH;
    tempCanvas.height = BARCODE_HEIGHT;
    
    // Generate barcodes and place them on A4
    for (let i = 0; i < quantity; i++) {
      const row = Math.floor(i / cols);
      const col = i % cols;
      
      const x = GAP + col * (BARCODE_WIDTH + GAP);
      const y = GAP + row * (BARCODE_HEIGHT + GAP);
      
      // Clear temp canvas
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.fillStyle = '#ffffff';
      tempCtx.fillRect(0, 0, BARCODE_WIDTH, BARCODE_HEIGHT);
      
      // Generate barcode on temp canvas
      try {
        JsBarcode(tempCanvas, barcodeValue, {
          format: "CODE128",
          width: barcodeSize,
          height: 80,
          displayValue: true,
          fontSize: 16,
          textMargin: 4,
          margin: 10,
          background: "#ffffff",
          lineColor: "#000000"
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
        continue;
      }
      
      // Draw barcode on A4 canvas
      ctx.drawImage(tempCanvas, x, y, BARCODE_WIDTH, BARCODE_HEIGHT);
    }
    
    // Draw cut lines after all barcodes are placed (to avoid overlaps)
    ctx.strokeStyle = '#999999';
    ctx.lineWidth = 0.5;
    ctx.setLineDash([3, 3]); // Dashed line pattern
    
    // Draw horizontal cut lines (between rows)
    for (let r = 1; r < rows; r++) {
      const lineY = GAP + r * (BARCODE_HEIGHT + GAP) - GAP / 2;
      ctx.beginPath();
      ctx.moveTo(GAP, lineY);
      ctx.lineTo(A4_WIDTH - GAP, lineY);
      ctx.stroke();
    }
    
    // Draw vertical cut lines (between columns)
    for (let c = 1; c < cols; c++) {
      const lineX = GAP + c * (BARCODE_WIDTH + GAP) - GAP / 2;
      ctx.beginPath();
      ctx.moveTo(lineX, GAP);
      ctx.lineTo(lineX, GAP + rows * (BARCODE_HEIGHT + GAP) - GAP);
      ctx.stroke();
    }
    
    // Draw corner cut indicators (small dashes at page corners)
    ctx.setLineDash([5, 2]);
    const cornerSize = 10;
    
    // Top-left corner
    ctx.beginPath();
    ctx.moveTo(GAP, GAP);
    ctx.lineTo(GAP + cornerSize, GAP);
    ctx.moveTo(GAP, GAP);
    ctx.lineTo(GAP, GAP + cornerSize);
    ctx.stroke();
    
    // Top-right corner
    ctx.beginPath();
    ctx.moveTo(A4_WIDTH - GAP, GAP);
    ctx.lineTo(A4_WIDTH - GAP - cornerSize, GAP);
    ctx.moveTo(A4_WIDTH - GAP, GAP);
    ctx.lineTo(A4_WIDTH - GAP, GAP + cornerSize);
    ctx.stroke();
    
    // Bottom-left corner
    const bottomY = GAP + rows * (BARCODE_HEIGHT + GAP) - GAP;
    ctx.beginPath();
    ctx.moveTo(GAP, bottomY);
    ctx.lineTo(GAP + cornerSize, bottomY);
    ctx.moveTo(GAP, bottomY);
    ctx.lineTo(GAP, bottomY - cornerSize);
    ctx.stroke();
    
    // Bottom-right corner
    ctx.beginPath();
    ctx.moveTo(A4_WIDTH - GAP, bottomY);
    ctx.lineTo(A4_WIDTH - GAP - cornerSize, bottomY);
    ctx.moveTo(A4_WIDTH - GAP, bottomY);
    ctx.lineTo(A4_WIDTH - GAP, bottomY - cornerSize);
    ctx.stroke();
    
    ctx.setLineDash([]); // Reset line dash
    
    // Download the A4 page
    const link = document.createElement('a');
    link.download = `${item.name || 'item'}-barcodes-${quantity}-A4.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    
    message.success(`Downloaded A4 page with ${quantity} barcode(s)`);
  };

  if (!barcodeValue) {
    return (
      <Modal
        title={
          <Space>
            <BarcodeOutlined />
            Barcode Generator - {item.name}
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={600}
        footer={null}
      >
        <Card>
          <Text type="warning">This item does not have a barcode assigned. Please add a barcode first.</Text>
        </Card>
      </Modal>
    );
  }

  return (
    <Modal
      title={
        <Space>
          <BarcodeOutlined />
          Barcode Generator - {item.name}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={600}
      footer={null}
      className="barcode-modal"
    >
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>
        <Card title="Item Barcode" size="small">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-md)' }}>
            <div style={{ 
              display: 'inline-block', 
              padding: '20px', 
              background: '#fff', 
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              marginBottom: 'var(--spacing-md)'
            }}>
              <canvas 
                ref={canvasRef} 
                style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                width={400}
                height={120}
              />
            </div>
            
            <div style={{ marginBottom: 'var(--spacing-sm)' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong>Barcode Width:</Text>
                  <Input
                    type="number"
                    value={barcodeSize}
                    onChange={(e) => setBarcodeSize(Number(e.target.value))}
                    min={1}
                    max={5}
                    step={0.5}
                    style={{ width: '80px', marginLeft: '8px' }}
                  />
                  <Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                    (1 = thin, 5 = thick)
                  </Text>
                </div>
                <div>
                  <Text strong>Quantity (for A4 page):</Text>
                  <InputNumber
                    min={1}
                    max={maxQuantity}
                    value={quantity}
                    onChange={(value) => setQuantity(value || 1)}
                    style={{ width: '100px', marginLeft: '8px' }}
                  />
                  <Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                    (Max: {maxQuantity} - based on stock)
                  </Text>
                </div>
              </Space>
            </div>
          </div>
          
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Text strong>Purpose:</Text>
              <Paragraph style={{ margin: 0, fontSize: '12px' }}>
                Print this barcode and place it on the product. Scanners can read it for quick item lookup.
              </Paragraph>
            </div>
            
            <div>
              <Text strong>Barcode Number:</Text>
              <Input.Group compact>
                <Input
                  value={barcodeValue}
                  readOnly
                  style={{ width: 'calc(100% - 32px)' }}
                />
                <Tooltip title="Copy Barcode">
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(barcodeValue, 'Barcode')}
                  />
                </Tooltip>
              </Input.Group>
            </div>
            
            <Space style={{ width: '100%', justifyContent: 'center' }}>
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadBarcode}
              >
                Download Single
              </Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={generateA4Page}
                type="primary"
                disabled={quantity < 1}
              >
                Download A4 Page ({quantity} barcode{quantity !== 1 ? 's' : ''})
              </Button>
            </Space>
          </Space>
        </Card>
      </div>

      {/* Usage Instructions */}
      <Card title="Usage Instructions" size="small" style={{ marginTop: 'var(--spacing-lg)' }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <Title level={5}>How it Works:</Title>
            <ul style={{ fontSize: '12px', paddingLeft: '16px', margin: 0 }}>
              <li>Print the barcode and place it on the product or shelf</li>
              <li>Use a barcode scanner to quickly look up the item</li>
              <li>The barcode number is displayed below the bars for manual entry</li>
              <li>Works with standard retail barcode scanners</li>
            </ul>
          </div>
          <div style={{ textAlign: 'center', marginTop: 'var(--spacing-sm)' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              💡 Recommended print size: Ensure barcode is at least 2cm wide for reliable scanning
            </Text>
          </div>
        </Space>
      </Card>
    </Modal>
  );
};

export default BarcodeModal;

